
import { Component, Prop, Vue } from 'vue-property-decorator';
import { SubscriptionPlanModel } from '@/models/subscriptionPlan/SubscriptionPlanModel';
import { subscriptionPlanApi } from '@/api/SubscriptionPlanApi';
import { notificationStore } from '@/store';
import { NotificationModel } from '@/models/notification/NotificationModel';
import { CustomerModel } from '@/models/customer/CustomerModel';
import { CustomerApi } from '@/api/CustomerApi';
import { Currency, CustomerCountry } from '@/../types/enums';
import { PlanFeature } from '@best-athletes/ba-types';
import { PLAN_FEATURES } from '@/../types/constants/plan-features';
import { PageState } from '@/models/PageState';
import { BASIC_PLAN_KEY } from '@/../types/constants/plan-keys';

@Component({
	render(h){
		return h(
			this.as,
			this.$slots.default ? this.$slots.default : this.$scopedSlots.default(this.SlotProps)
		);
	}
})
export default class SubscriptionPlanInfoProvider extends Vue{
	@Prop({ default: 'div' }) as: string;
	/** Parent Resource of the Customer ie. 'athlete' */
	@Prop({ default: null }) resource: string | null;
	/** Parent ID of the Customer ie. athlete.id */
	@Prop({ default: null }) parentId: string | null;
	@Prop({ default: null }) country: CustomerCountry | null;

	get Country(): CustomerCountry {
		if( !this.country ) return CustomerCountry.CA;
		return this.country;
	}

	get CountryCurrency(): Currency{
		if( this.Country === CustomerCountry.CA ) {
			return Currency.CAD;
		}
		return Currency.USD;
	}

	get CustomerApi(): CustomerApi | null{
		if(this.resource === null || this.parentId === null){
			return null;
		}
		return new CustomerApi(`${this.resource}/${this.parentId}`);
	}

	get plansLoading(): boolean{
		return this.state.IsLoading;
	}
	get plansInitialized(): boolean{
		return this.state.IsReady;
	}
	plans: SubscriptionPlanModel[] = [];
	basicPlan: SubscriptionPlanModel;
	customer: CustomerModel | null = null;
	state: PageState = new PageState("Initial");

	get PlanPricesFormatted(): string[]{
		if(this.customer === null){
			// TODO: Review prices for new users. This will default to CAD prices when on the upgrade page
			return this.plans.map(plan => plan.getPriceFormatted(this.CountryCurrency));
		}
		return this.plans.map(plan => plan.getPriceFormatted(this.customer.currency));
	}
	get MonthlyPlanPricesFormatted(): string[]{
		if(this.customer === null){
			// TODO: Review prices for new users. This will default to CAD prices when on the upgrade page
			return this.plans.map(plan => plan.getMonthlyPriceFormatted(this.CountryCurrency));
		}
		return this.plans.map(plan => plan.getMonthlyPriceFormatted(this.customer.currency));
	}
	/** Prices to display slashed down, if any*/
	get SlashPricesFormatted(): Array<string | null>{
		if(this.customer === null){
			// TODO: Review prices for new users. This will default to CAD prices when on the upgrade page
			return this.plans.map(plan => plan.getSlashPriceFormatted(this.CountryCurrency));
		}
		return this.plans.map(plan => plan.getSlashPriceFormatted(this.customer.currency));
	}

	get Ready(): boolean{
		return this.plansLoading === false && this.plansInitialized === true;
	}

	created(): void{
		this.loadPlanInformation();
	}

	async loadPlanInformation(): Promise<void>{
		this.state = new PageState('Loading');
		try{
			if(this.CustomerApi !== null){
				// If no resource/parent id is provided, only load plan info
				this.customer = await this.CustomerApi.getCustomerOrCreate();
			}
			const config = { params: { populate: 'prices', country: this.Country } };
			this.plans = await subscriptionPlanApi.findAllWithAccess(config);
			const basicPlan: SubscriptionPlanModel = this.plans.find( p => p.key === BASIC_PLAN_KEY);
			this.basicPlan = new SubscriptionPlanModel().load(basicPlan) ;
			this.state = new PageState('Ready');
		}catch(error){
			console.error("Failed to load plans", error);
			this.state = PageState.getPageState(error);
			notificationStore.pushErrorNotification(new NotificationModel("Something went wrong. Please try again later"));
		}
	}

	get SlotProps(): {
		state: PageState,
		AllFeatures: PlanFeature[],
		plansLoading: boolean,
		plansInitialized: boolean,
		plans: SubscriptionPlanModel[],
		basicPlan: SubscriptionPlanModel,
		customer: CustomerModel,
		Ready: boolean,
		PlanPricesFormatted: string[],
		MonthlyPlanPricesFormatted: string[],
		SlashPricesFormatted: Array<string | null>,
	}{
		return {
			state: this.state,
			AllFeatures: PLAN_FEATURES,
			plansLoading: this.plansLoading,
			plansInitialized: this.plansInitialized,
			plans: this.plans,
			basicPlan: this.basicPlan,
			customer: this.customer,
			Ready: this.Ready,
			PlanPricesFormatted: this.PlanPricesFormatted,
			MonthlyPlanPricesFormatted: this.MonthlyPlanPricesFormatted,
			SlashPricesFormatted: this.SlashPricesFormatted,
		};
	}
}
